/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/Sections/CommercialOffer/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../../utils/logger.js";
import React, { useState, useEffect, useMemo, useRef } from "react";
import styles from "./CommercialOffer.module.css";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  getExportsOptions,
  getImportsOptions,
  getSellsToGovernmentOptions,
  getCompanyGeneralActivityOptions,
  getCountryExportingToOptions,
  getCountryImportingFromOptions,
} from "../../../data/selectOptions";
import {
  getSelectedOption,
  getSelectedOptionWithZeroFirst,
  selectStyles,
  getCompanyFieldValue,
} from "../../../utils/common";
import { useFieldValidation } from "../../../hooks/useFieldValidation";

const CommercialOffer = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const { t, i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const prevHasErrorsRef = useRef(false);

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    productoPrincipal: "",
    materiaPrima: "",
    certificacionesDeCalidad: "",
  });

  const [principalProducts, setPrincipalProducts] = useState(() => {
    try {
      const companyPrincipalProducts = getCompanyFieldValue(
        company,
        "productoPrincipal",
        selectedProfile,
        {},
      );

      const languages = ["es", "en", "fr"];

      languages.forEach((lang) => {
        if (!companyPrincipalProducts[lang]) {
          companyPrincipalProducts[lang] = "";
        }
      });

      return companyPrincipalProducts;
    } catch (error) {
      logger.error("Error parsing principal products:", {
        err: sanitizeError(error),
      });
      return {
        es: "",
        en: "",
        fr: "",
      };
    }
  });

  const companyPrincipalProducts = useMemo(() => {
    const products = getCompanyFieldValue(
      company,
      "productoPrincipal",
      selectedProfile,
      null,
    );
    if (products) {
      return products;
    }
    return {
      es: "",
      en: "",
      fr: "",
    };
  }, [company?.productoPrincipal, company?.perfiles, selectedProfile]);

  useEffect(() => {
    setPrincipalProducts(companyPrincipalProducts);
  }, [companyPrincipalProducts]);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  // Notify parent of validation changes only when the state actually changes
  useEffect(() => {
    if (onValidationChange && prevHasErrorsRef.current !== hasErrors) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);

  const handlePrincipalProductsChange = (value) => {
    const newPrincipalProducts = { ...principalProducts };
    newPrincipalProducts[currentLanguage] = value;
    setPrincipalProducts(newPrincipalProducts);
    handleInputChange("productoPrincipal", newPrincipalProducts);
  };

  const [materiaPrima, setMateriaPrima] = useState(
    getCompanyFieldValue(company, "materiaPrima", selectedProfile, ""),
  );
  const [certificacionesDeCalidad, setCertificacionesDeCalidad] = useState(
    getCompanyFieldValue(
      company,
      "certificacionesDeCalidad",
      selectedProfile,
      "",
    ),
  );

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const exportsOptions = getExportsOptions(t);
  const importsOptions = getImportsOptions(t);
  const sellsToGovernmentOptions = getSellsToGovernmentOptions(t);
  const companyGeneralActivityOptions = getCompanyGeneralActivityOptions(t);
  const countryExportingToOptions = getCountryExportingToOptions(t);
  const countryImportingFromOptions = getCountryImportingFromOptions(t);

  const [actividadGeneralDeLaEmpresa, setactividadGeneralDeLaEmpresa] =
    useState(
      getCompanyFieldValue(
        company,
        "actividadGeneralDeLaEmpresa",
        selectedProfile,
        null,
      ),
    );
  const [imports, setImports] = useState(
    getCompanyFieldValue(company, "importa", selectedProfile, null),
  );
  const [exports, setExports] = useState(
    getCompanyFieldValue(company, "exporta", selectedProfile, null),
  );
  const [sellsToGovernment, setSellsToGovernment] = useState(
    getCompanyFieldValue(company, "vendeAGobierno", selectedProfile, null),
  );
  const [countryExportingTo, setCountryExportingTo] = useState(
    getCompanyFieldValue(company, "paisExportacion", selectedProfile, null),
  );
  const [countryImportingFrom, setCountryImportingFrom] = useState(
    getCompanyFieldValue(company, "paisImportacion", selectedProfile, null),
  );

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.commercialOffer.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.inputRow}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.companyGeneralActivityLabel")}
          </label>
          <div className={styles.inputWrapper}>
            <Select
              value={getSelectedOptionWithZeroFirst(
                companyGeneralActivityOptions,
                actividadGeneralDeLaEmpresa,
              )}
              options={companyGeneralActivityOptions}
              placeholder={t(
                "components.commercialOffer.companyGeneralActivityPlaceholder",
              )}
              styles={selectStyles}
              className="select__control"
              onChange={(selectedOption) => {
                setactividadGeneralDeLaEmpresa(selectedOption.value);
                handleInputChange(
                  "actividadGeneralDeLaEmpresa",
                  selectedOption.value,
                );
              }}
              isDisabled={true}
            />
          </div>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.principalProductsLabel")}
          </label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={`${styles.input} ${
                getFieldValidationState(
                  "productoPrincipal",
                  principalProducts[currentLanguage] || "",
                ) === "error"
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.commercialOffer.principalProductsPlaceholder",
              )}
              value={principalProducts[currentLanguage] || ""}
              onChange={(e) => {
                const value = e.target.value;
                handlePrincipalProductsChange(value);
                handleFieldChange("productoPrincipal", value);
              }}
              maxLength={200}
            />
            {getErrorMessage("productoPrincipal") && (
              <p className={styles.errorMessage}>
                {getErrorMessage("productoPrincipal")}
              </p>
            )}
          </div>
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.rawMaterialLabel")}
          </label>
          <input
            type="text"
            className={`${styles.input} ${styles.inputWithoutIcon} ${
              styles.tabletRawMaterialInput
            } ${
              getFieldValidationState("materiaPrima", materiaPrima) === "error"
                ? styles.inputError
                : ""
            }`}
            placeholder={t("components.commercialOffer.rawMaterialPlaceholder")}
            value={materiaPrima}
            onChange={(e) => {
              const value = e.target.value;
              setMateriaPrima(value);
              handleFieldChange("materiaPrima", value);
              handleInputChange("materiaPrima", value);
            }}
            maxLength={200}
          />
          {getErrorMessage("materiaPrima") && (
            <p className={styles.errorMessage}>
              {getErrorMessage("materiaPrima")}
            </p>
          )}
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.exportsLabel")}
          </label>
          <Select
            value={getSelectedOption(exportsOptions, exports)}
            options={exportsOptions}
            placeholder={t("components.commercialOffer.exportsPlaceholder")}
            styles={selectStyles}
            className="select__control"
            onChange={(selectedOption) => {
              setExports(selectedOption.value);
              handleInputChange("exporta", selectedOption.value);
            }}
          />
        </div>
      </div>
      <div className={`${styles.inputRow} ${styles.specialSpacing}`}>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.countryExportingToLabel")}
          </label>
          <Select
            value={getSelectedOption(
              countryExportingToOptions,
              countryExportingTo,
            )}
            options={countryExportingToOptions}
            placeholder={t(
              "components.commercialOffer.countryExportingToPlaceholder",
            )}
            styles={selectStyles}
            className="select__control"
            onChange={(selectedOption) => {
              setCountryExportingTo(selectedOption.value);
              handleInputChange("paisExportacion", selectedOption.value);
            }}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.importsLabel")}
          </label>
          <Select
            value={getSelectedOption(importsOptions, imports)}
            options={importsOptions}
            placeholder={t("components.commercialOffer.importsPlaceholder")}
            styles={selectStyles}
            className="select__control"
            onChange={(selectedOption) => {
              setImports(selectedOption.value);
              handleInputChange("importa", selectedOption.value);
            }}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.countryImportingFromLabel")}
          </label>
          <Select
            value={getSelectedOption(
              countryImportingFromOptions,
              countryImportingFrom,
            )}
            options={countryImportingFromOptions}
            placeholder={t(
              "components.commercialOffer.countryImportingFromPlaceholder",
            )}
            styles={selectStyles}
            className="select__control"
            onChange={(selectedOption) => {
              setCountryImportingFrom(selectedOption.value);
              handleInputChange("paisImportacion", selectedOption.value);
            }}
          />
        </div>
        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            {t("components.commercialOffer.sellsToGovernmentLabel")}
          </label>
          <Select
            value={getSelectedOption(
              sellsToGovernmentOptions,
              sellsToGovernment,
            )}
            options={sellsToGovernmentOptions}
            placeholder={t(
              "components.commercialOffer.sellsToGovernmentPlaceholder",
            )}
            styles={selectStyles}
            className="select__control"
            onChange={(selectedOption) => {
              setSellsToGovernment(selectedOption.value);
              handleInputChange("vendeAGobierno", selectedOption.value);
            }}
          />
        </div>
      </div>
      <div
        className={`${styles.inputRow} ${styles.twoInputsRow} ${styles.lastRow}`}
      >
        <div className={`${styles.inputGroup} ${styles.twoLabelsGroup}`}>
          <label
            className={`${styles.inputLabel} ${styles.tabletCertificationsLabel}`}
          >
            {t("components.commercialOffer.qualityCertificationsLabel")}
          </label>
          <div className={styles.inputWrapper}>
            <input
              type="text"
              className={`${styles.input} ${styles.tabletCertificationsInput} ${
                getFieldValidationState(
                  "certificacionesDeCalidad",
                  certificacionesDeCalidad,
                ) === "error"
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t(
                "components.commercialOffer.qualityCertificationsPlaceholder",
              )}
              value={certificacionesDeCalidad}
              onChange={(e) => {
                const value = e.target.value;
                setCertificacionesDeCalidad(value);
                handleInputChange("certificacionesDeCalidad", value);
                handleFieldChange("certificacionesDeCalidad", value);
              }}
              maxLength={200}
            />
            {getErrorMessage("certificacionesDeCalidad") && (
              <p className={styles.errorMessage}>
                {getErrorMessage("certificacionesDeCalidad")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommercialOffer;
